import { useTranslation } from 'react-i18next';
import type { PushState } from '../push/usePush.js';

/**
 * Der Knopf, mit dem Erinnerungen erst wirklich Erinnerungen werden.
 *
 * Jeder Zustand bekommt einen eigenen, ehrlichen Text. „Geht nicht" ohne Grund
 * ist die Sorte Meldung, nach der Leute die Funktion für kaputt halten — dabei
 * fehlt auf dem iPhone meist nur die Installation auf dem Home-Bildschirm.
 */
export function PushSetup({ state }: { state: PushState }): React.JSX.Element | null {
  const { t } = useTranslation();

  if (state.status === 'enabled') {
    // Gemessen statt behauptet: Wie viele Aktions-Knöpfe die Umgebung in einer
    // Benachrichtigung zulässt. 0 heißt: Antippen ist der einzige Weg.
    //
    // Fehlt die Eigenschaft ganz — auf iOS ist das der Fall —, wird das
    // ausdrücklich gesagt. Ein leerer Platz sähe aus, als sei die Frage
    // beantwortet; „keine Angabe" ist aber etwas anderes als „null Knöpfe".
    const maxActions =
      typeof Notification !== 'undefined' && 'maxActions' in Notification
        ? (Notification as unknown as { maxActions: number }).maxActions
        : null;

    return (
      <div className="kw-tile">
        {/* Die Überschrift fehlte im eingerichteten Zustand — dadurch war der
            Abschnitt nicht mehr auffindbar, sobald er funktionierte. */}
        <h2 className="kw-kicker">{t('push.heading')}</h2>
        <p className="mt-1.5 text-sm font-semibold text-good" role="status">
          {t('push.enabled')}
        </p>
        <p className="mt-1 text-xs text-ink-muted">
          {maxActions !== null
            ? t('push.maxActions', { count: maxActions })
            : t('push.maxActionsUnknown')}
        </p>
      </div>
    );
  }

  const beschreibung: Record<string, string> = {
    unsupported: t('push.unsupported'),
    'needs-install': t('push.needsInstall'),
    'not-configured': t('push.notConfigured'),
    denied: t('push.denied'),
    error: t('push.error', { message: state.error ?? '' }),
  };

  const kannTippen = state.status === 'idle' || state.status === 'enabling';

  return (
    <div className="kw-tile">
      <h2 className="kw-kicker">{t('push.heading')}</h2>
      <p className="mt-1.5 text-sm">{beschreibung[state.status] ?? t('push.intro')}</p>

      {kannTippen ? (
        <button
          type="button"
          onClick={state.enable}
          disabled={state.status === 'enabling'}
          className="kw-btn mt-3 w-full"
        >
          {state.status === 'enabling' ? t('push.enabling') : t('push.enable')}
        </button>
      ) : null}
    </div>
  );
}
